import { useEffect, useRef, useState } from "react";

type FeaturePoint = readonly [x: number, y: number, driftX: number, driftY: number];
const MESH_EDGES = [[0, 1], [1, 2], [2, 3], [3, 4], [4, 0], [0, 2], [1, 4], [1, 3], [0, 3]] as const;
const RIGHT_POINTS: readonly FeaturePoint[] = [[902, 478, -3, 2], [923, 416, 2, -3], [980, 403, -2, 3], [998, 461, 3, 2], [959, 513, -2, -4]];

function PointDrift({ attribute, value, delta, duration }: { attribute: string; value: number; delta: number; duration: number }) {
  return <animate attributeName={attribute} values={`${value};${value + delta};${value - delta * .5};${value}`}
    keyTimes="0;.33;.66;1" calcMode="spline" keySplines=".42 0 .58 1;.42 0 .58 1;.42 0 .58 1" dur={`${duration}s`} repeatCount="indefinite" />;
}

function FeatureMesh({ points, motion, duration }: { points: readonly FeaturePoint[]; motion: boolean; duration: number }) {
  return <g>
    {[...MESH_EDGES, MESH_EDGES[5]].map(([a, b], index) => {
      const from = points[a]!;
      const to = points[b]!;
      return <line key={index} className={index === MESH_EDGES.length ? "login-vision-mesh-trace" : undefined}
        pathLength="1" x1={from[0]} y1={from[1]} x2={to[0]} y2={to[1]}>
        {motion && <>
          <PointDrift attribute="x1" value={from[0]} delta={from[2]} duration={duration} />
          <PointDrift attribute="y1" value={from[1]} delta={from[3]} duration={duration} />
          <PointDrift attribute="x2" value={to[0]} delta={to[2]} duration={duration} />
          <PointDrift attribute="y2" value={to[1]} delta={to[3]} duration={duration} />
        </>}
      </line>;
    })}
    {points.map(([cx, cy, dx, dy], index) => <circle key={index} cx={cx} cy={cy} r="3" fill="var(--canvas)">
      {motion && <>
        <PointDrift attribute="cx" value={cx} delta={dx} duration={duration} />
        <PointDrift attribute="cy" value={cy} delta={dy} duration={duration} />
      </>}
    </circle>)}
  </g>;
}

/** Decorative feature geometry, with no simulated detections or live-status claims. */
export function LoginVisionBackdrop() {
  const backdrop = useRef<HTMLDivElement>(null);
  const [motionEnabled, setMotionEnabled] = useState(() => !window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  useEffect(() => {
    const root = backdrop.current;
    const area = root?.parentElement;
    if (!root || !area) return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const pointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    let frame = 0;
    let shiftX = 0;
    let shiftY = 0;
    const paint = () => {
      frame = 0;
      root.style.setProperty("--vision-shift-x", `${shiftX}px`);
      root.style.setProperty("--vision-shift-y", `${shiftY}px`);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(paint); };
    const reset = () => { shiftX = 0; shiftY = 0; schedule(); };
    const motionChange = () => { reset(); setMotionEnabled(!motion.matches); };
    const move = (event: PointerEvent) => {
      if (motion.matches || !pointer.matches || event.pointerType === "touch") return;
      const bounds = area.getBoundingClientRect();
      shiftX = Math.max(-1, Math.min(1, (event.clientX - bounds.left) / bounds.width * 2 - 1)) * 6;
      shiftY = Math.max(-1, Math.min(1, (event.clientY - bounds.top) / bounds.height * 2 - 1)) * 5;
      schedule();
    };
    area.addEventListener("pointermove", move, { passive: true });
    area.addEventListener("pointerleave", reset);
    motion.addEventListener("change", motionChange);
    pointer.addEventListener("change", reset);
    return () => {
      cancelAnimationFrame(frame);
      area.removeEventListener("pointermove", move);
      area.removeEventListener("pointerleave", reset);
      motion.removeEventListener("change", motionChange);
      pointer.removeEventListener("change", reset);
    };
  }, []);

  return <div className="login-vision" ref={backdrop} aria-hidden="true">
    <svg viewBox="0 0 1120 680" fill="none">
      <defs>
        <linearGradient id="login-scan-edge"><stop stopColor="currentColor" stopOpacity="0" /><stop offset=".5" stopColor="currentColor" /><stop offset="1" stopColor="currentColor" stopOpacity="0" /></linearGradient>
        <linearGradient id="login-patch-light" x2="100%" y2="100%"><stop stopColor="currentColor" stopOpacity=".14" /><stop offset="1" stopColor="currentColor" stopOpacity=".02" /></linearGradient>
        <radialGradient id="login-vision-fade"><stop offset=".6" stopColor="white" /><stop offset="1" stopColor="white" stopOpacity="0" /></radialGradient>
        <mask id="login-vision-mask"><rect width="1120" height="680" fill="url(#login-vision-fade)" /></mask>
      </defs>
      <g mask="url(#login-vision-mask)">
        <g className="login-vision-patches" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
          <g opacity=".6">
            {[0, 1, 2, 3].map(index => <rect key={index} x={119 + index % 2 * 55} y={211 + Math.floor(index / 2) * 51} width="47" height="43" rx="6" />)}
            <path d="m126 243 10-12 8 5 12-15m-24 23 10-6 13 5M180 239c-9-9-3-19 8-18 10 1 13 9 4 20m-12-2 12-17M128 292l7-20 17-2 6 21-15 5zM181 277c5-9 15-7 16 1s-7 16-15 13m-1-14 11 11" />
          </g>
          <g className="login-vision-patch-landmarks" fill="var(--canvas)">
            <circle cx="136" cy="231" r="2" /><circle cx="156" cy="221" r="2" />
            <circle cx="188" cy="221" r="2" /><circle cx="192" cy="241" r="2" />
          </g>
          <g className="login-vision-patch-focus">
            <rect className="login-vision-patch-light" x="119" y="211" width="47" height="43" rx="6" stroke="none" />
            <path d="M126 204h-14v14m0 29v14h14m33-57h14v14m0 29v14h-14" />
          </g>
        </g>
        <g className="login-vision-features" stroke="currentColor" strokeWidth="1">
          <FeatureMesh points={RIGHT_POINTS} motion={motionEnabled} duration={29} />
        </g>
        <g className="login-vision-classification" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="912" cy="194" r="2.5" fill="var(--canvas)" />
          <path d="M918 194h11m0 0 12-17h15m-27 17 12 20h15" opacity=".45" />
          <path className="login-vision-class-trace" d="M918 194h11l12-17h15" pathLength="1" />
          <g className="login-vision-class-group">
            <circle cx="967" cy="173" r="3" /><circle cx="980" cy="168" r="3" /><circle cx="976" cy="182" r="3" /><circle cx="989" cy="178" r="3" />
          </g>
          <g className="login-vision-class-group login-vision-class-group--second">
            <path d="m967 206 4 4-4 4-4-4zm15 2 4 4-4 4-4-4zm-7 13 4 4-4 4-4-4zm15-9 4 4-4 4-4-4z" />
          </g>
        </g>
        <g className="login-vision-scan"><path d="M80 90h960" stroke="url(#login-scan-edge)" strokeWidth="1" /></g>
      </g>
    </svg>
  </div>;
}
